<template>
  <div class="bz-screen">
    <header class="bz-screen__bar" :class="{ 'is-collapsed': collapsed }">
      <div class="bz-screen__bar-inner">
        <BzButton
          v-if="backLabel"
          kind="secondary"
          icon="arrow_back"
          :aria-label="backLabel"
          :data-testid="backTestid"
          @click="$emit('back')"
        />
        <!-- The name again, small, once the big one has scrolled away; a screen reader has the heading. -->
        <span class="bz-screen__bar-title" aria-hidden="true">{{ title }}</span>
        <div class="bz-screen__actions">
          <slot name="actions" />
        </div>
      </div>
    </header>
    <div ref="scroller" class="bz-screen__scroll" :data-testid="scrollTestid">
      <div class="bz-screen__column">
        <h1 ref="heading" class="bz-screen__title">{{ title }}</h1>
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import BzButton from './BzButton.vue';

/**
 * A full screen of the design system (Settings, the catalog; from stage 4 Debug, the queue, help): a
 * bar with back and its actions, the screen's name big at the top of its content, and the content in a
 * column of readable width.
 *
 * @rule   Back is a round secondary button with a name (`backLabel`), 48 px; at most two more actions,
 *         in `#actions`, as round icon buttons too.
 * @rule   The name is the screen's <h1>, large, at the head of the column it scrolls with; once it has
 *         scrolled away, the bar shows it small next to back and wears a hairline at its foot. That is
 *         the large title both systems collapse (iOS's, and Android's collapsing top bar in its
 *         settings), and the column lines up with the bar, so the screen has one left edge.
 * @why    An IntersectionObserver on the heading, not a scroll listener: nothing runs while the list
 *         scrolls but the browser's own check, and the change is two opacities.
 */
export default {
  name: 'BzScreen',
  components: { BzButton },
  props: {
    title: { type: String, required: true },
    backLabel: { type: String, default: undefined },
    backTestid: { type: String, default: undefined },
    scrollTestid: { type: String, default: undefined },
  },
  emits: ['back'],
  data() {
    return { collapsed: false, observer: null };
  },
  mounted() {
    if (typeof IntersectionObserver === 'undefined') return;
    // The last entry is the heading's state now: a busy main thread hands several crossings at once.
    this.observer = new IntersectionObserver((entries) => {
      this.collapsed = !entries[entries.length - 1].isIntersecting;
    }, { root: this.$refs.scroller, threshold: 0 });
    this.observer.observe(this.$refs.heading);
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
};
</script>

<style scoped>
.bz-screen {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  /* DM Sans's optical sizes at every size (its own spacing for a 12 px hint and a 36 px title); a weight
     or a slant it lacked would be the nearest it has, never a smeared fake; kerning always. */
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
}

.bz-screen__bar {
  position: relative;
  flex-shrink: 0;
  /* Under an iPad's status bar or a notch, never behind it (norm 10). */
  padding-top: env(safe-area-inset-top, 0px);
  background: var(--bz-bg);
}

/* The hairline the bar wears once the big title has scrolled under it. */
.bz-screen__bar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: var(--bz-edge);
  opacity: 0;
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
}

.bz-screen__bar.is-collapsed::after {
  opacity: 1;
}

.bz-screen__bar-inner {
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
  box-sizing: border-box;
  max-width: var(--bz-content-max);
  min-height: var(--bz-bar-height);
  margin: 0 auto;
  padding: var(--bz-space-2) var(--bz-space-6);
}

/* The name in the bar: a row title's size at a title's weight (iOS's inline title is 17 semibold), faded
   in once the big one is gone. Hidden, not only transparent, while the big one shows: no selection
   handles on invisible text, nothing for find-in-page. */
.bz-screen__bar-title {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--bz-font-display);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out), visibility 0s linear var(--bz-duration-fast);
}

.bz-screen__bar.is-collapsed .bz-screen__bar-title {
  opacity: 1;
  visibility: visible;
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out), visibility 0s;
}

/* The name, large, at the head of the content: 28/36, the top of the titles' ladder (14, 18, 22, 28, each
   step ×1.22–1.29; 16 is the body's and the values', off the ladder). At 36 it shouted over the
   restaurant's 22 and the rows' 18. */
.bz-screen__title {
  margin: 0 0 var(--bz-space-6);
  font-family: var(--bz-font-display);
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

.bz-screen__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
}

.bz-screen__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.bz-screen__column {
  box-sizing: border-box;
  max-width: var(--bz-content-max);
  margin: 0 auto;
  padding: var(--bz-space-4) var(--bz-space-6) var(--bz-space-8);
}

@media (max-width: 599px) {
  .bz-screen__bar-inner,
  .bz-screen__column {
    padding-left: var(--bz-space-4);
    padding-right: var(--bz-space-4);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bz-screen__bar::after,
  .bz-screen__bar-title,
  .bz-screen__bar.is-collapsed .bz-screen__bar-title {
    transition: none;
  }
}
</style>
